import React, { useEffect, useState } from "react";
import { FaSearch, FaUserFriends } from "react-icons/fa";
import useAxios from "../api/useAxios";

const Referrals = () => {
  const axios = useAxios();
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    fetchReferrals();
  }, []);

  const fetchReferrals = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await axios.get("/referrals");
      if (res.data?.success) {
        setReferrals(Array.isArray(res.data.data) ? res.data.data : []);
      } else {
        setReferrals([]);
        setError(res.data?.message || "Failed to fetch referrals");
      }
    } catch (err) {
      console.error(err);
      setReferrals([]);
      setError("Unable to load referrals. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const formatPhone = (phone) => {
    if (!phone) return "-";
    const cleaned = String(phone).replace(/\D/g, "");
    if (cleaned.length === 10) return `(${cleaned.slice(0,3)}) ${cleaned.slice(3,6)}-${cleaned.slice(6)}`;
    return phone;
  };

  const filtered = referrals.filter((r) => {
    const text = `${r.fullName || ""} ${r.email || ""} ${r.mobileNumber || ""} ${r.referredByUserId || ""}`.toLowerCase();
    return text.includes(search.toLowerCase());
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-6">
      <div className="mb-6">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Referrals</h1>
            <p className="text-gray-600 mt-1">View referred users and their referrers</p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={fetchReferrals}
              className="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg shadow hover:opacity-95"
            >
              Refresh
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
        <div className="relative max-w-2xl">
          <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search referrals by name, email, phone or referrer..."
            className="w-full pl-12 pr-4 py-3.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all"
          />
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[700px]">
            <thead className="bg-gradient-to-r from-gray-50 to-blue-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">#</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Full Name</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Mobile</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Email</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Referred By</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100">
              {loading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i} className="animate-pulse">
                    <td className="px-6 py-5">
                      <div className="h-4 bg-gray-200 rounded w-6"></div>
                    </td>
                    <td className="px-6 py-5">
                      <div className="h-4 bg-gray-200 rounded w-40"></div>
                    </td>
                    <td className="px-6 py-5">
                      <div className="h-4 bg-gray-200 rounded w-28"></div>
                    </td>
                    <td className="px-6 py-5">
                      <div className="h-4 bg-gray-200 rounded w-48"></div>
                    </td>
                    <td className="px-6 py-5">
                      <div className="h-4 bg-gray-200 rounded w-28"></div>
                    </td>
                  </tr>
                ))
              ) : filtered.length > 0 ? (
                filtered.map((r, idx) => (
                  <tr key={r.id || idx} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-6 py-5 text-sm text-gray-700">{idx + 1}</td>
                    <td className="px-6 py-5">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-100 to-blue-200 rounded-lg flex items-center justify-center">
                          <span className="font-semibold text-blue-600">{(r.fullName || "-")[0] || "-"}</span>
                        </div>
                        <div>
                          <div className="font-semibold text-gray-900">{r.fullName || r.name || "-"}</div>
                          <div className="text-sm text-gray-500 mt-1">{r.role || ""}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-5 text-sm text-gray-700">{formatPhone(r.mobileNumber)}</td>
                    <td className="px-6 py-5 text-sm text-gray-700">{r.email || "-"}</td>
                    <td className="px-6 py-5 text-sm text-gray-700">{r.referredUserName || r.referredBy || "-"}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="px-6 py-16 text-center">
                    <div className="flex flex-col items-center justify-center gap-4">
                      <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center">
                        <FaUserFriends className="w-12 h-12 text-gray-400" />
                      </div>
                      <h3 className="text-xl font-semibold text-gray-700">No referrals found</h3>
                      <p className="text-gray-500">{error ? error : (search ? "No referrals match your search." : "There are no referrals to show.")}</p>
                      <div className="flex items-center gap-3 mt-4">
                        <button onClick={fetchReferrals} className="px-4 py-2 bg-blue-600 text-white rounded-lg">Refresh</button>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Referrals;
